import React, { useEffect, useState } from "react";
import { CircularProgress, Grid, Typography } from "@mui/material";
import axiosSecond from "../../libs/axios";
import { motion } from "framer-motion";

const ordinalSuffix = (day) => {
  const j = day % 10;
  const k = day % 100;
  if (k >= 11 && k <= 13) return "<sup>th</sup>";
  if (j === 1) return "<sup>st</sup>";
  if (j === 2) return "<sup>nd</sup>";
  if (j === 3) return "<sup>rd</sup>";
  return "<sup>th</sup>";
};

const formatOrdinalDate = (date, { useUtc = false, includeTime = false } = {}) => {
  if (!date || Number.isNaN(date.getTime())) return "";

  const day = useUtc ? date.getUTCDate() : date.getDate();
  const month = date.toLocaleDateString("en-GB", {
    month: "long",
    ...(useUtc ? { timeZone: "UTC" } : {}),
  });
  const year = useUtc ? date.getUTCFullYear() : date.getFullYear();
  let formatted = `${day}${ordinalSuffix(day)} ${month} ${year}`;

  if (includeTime) {
    const time = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: useUtc ? "UTC" : undefined,
    });
    formatted = `${formatted}, ${time}`;
  }

  return formatted;
};

const PDFViewer = ({ data }) => {
  const [assessmentPdf, setAssessmentPdf] = useState(null);
  const [assessmentDownloadPdf, setAssessmentDownloadPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errormg, setErrormg] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  const get = async () => {
    if (!data) {
      setLoading(false);
      setErrormg(true);
      return;
    }

    const {
      Q1: q1 = "",
      Q2: q2 = "",
      Q3: q3 = "",
      Q4: q4 = "",
      Q5: q5 = "",
      Q6: q6 = "",
      Q7: q7 = "",
      Q9: q9 = "",
      Q10: q10 = "",
    } = data;

    function returnRI(q1, q2, q3, q4, q5, q6, q7, q9, q10) {
      const riData = [
        {
          code: "2022-1",
          title: "Instate an EPS Panel Management System.",
          priority: "Urgent",
          detail:
            "A regular inspection of the condition of Expanded Polystyrene (EPS) should be instated on site.\n\nAll damaged EPS panels should be promptly repaired or re-sealed.",
          risk_description:
            "Damage to the metal face of an EPS sandwich panel would leave the ignitable core exposed.\n\nIgnition sources such as smoking, embers from hot work or electrical faults could result in a fire if they come into contact with the EPS core.\n\nWhen exposed EPS burns, it generates a considerable amount of toxic, dense black smoke. Due to the intense heat of an EPS fire, combined with poor visibility and toxicity of the smoke produced, it is highly unlikely that the fire brigade would attempt to fight this fire. \n\nHistory suggests fire brigades would tend to focus on saving surrounding buildings.",
        },
        {
          code: "2022-2",
          title: "Instate a Hot Work Permit.",
          priority: "Urgent",
          detail:
            "A hot work permit system should be implemented on site for use by both staff, tenants and visitors.\n\nAll hot works should include:\n- Ignitable material within 15m of the hot work is removed or provided with safety screens and barriers prior to the commencement of works.\n- a mandatory fire-watch during and at least 30 mins after the completion of works.",
          risk_description:
            "Poorly controlled hot work operations such as cutting or welding are common causes of fires.\n\nSparks and embers originating from hot work could ignite nearby combustibles. If conducted in a poorly monitored or high hazard area, a small fire could quickly propagate into a large blaze, damaging equipment and contents, leading to subsequent downtime and loss of profits.",
        },
        {
          code: "2022-3",
          title: "Improve Contractor Management Practices.",
          priority: "Urgent",
          detail:
            "A documented contractor management program covering each of the following should be instated on site:\n- Contractor procurement\n- Contractor qualification review\n- Contracted job process\n- Appropriate insurances and trade licences attained by the contractor.\n\nThe following should be conducted prior to any work commencing on site:\n- Site induction and sign-in\n- Awareness training of site hazards\n- Expectations regarding risk management practices including hot works and emergency response procedures.\n\nAll records of contractors and works completed should be stored for reference.",
          risk_description:
            "As contractors often prioritise efficiency, shortcuts can be taken at time if not appropriately directed by the procurer.\n\nLoss history indicates that poorly controlled work by contractors has led to significant fires damaging physical property and downtime.",
        },
        {
          code: "2022-4",
          title: "Instate an Impairment Management System.",
          priority: "Urgent",
          detail:
            "An impairment management system should be instated to ensure precautionary steps are taken to protect the site in the event the sprinkler system, water supply or fire alarm system is offline.",
          risk_description:
            "The automatic sprinkler system is the site’s main defence against fire.\n\nThe site would be at its most vulnerable in the event the sprinkler system or its water supply is offline.",
        },
        {
          code: "2022-5",
          title: "Enforce a strict No Smoking Policy.",
          priority: "Urgent",
          detail:
            "Designated smoking areas should be assigned on site for smokers, along with the enforcement of a smoking policy.",
          risk_description:
            "Smoking is a common ignition source and has caused many fires.\nEven a small cigarette butts could ignite nearby material and could lead to a fire which propagates across the site.",
        },
        {
          code: "2022-6",
          title: "Improve general housekeeping practices.",
          priority: "Urgent",
          detail:
            "Every effort should be made to maintain clean and tidy work and storage areas to prevent the continuity of ignitable material.",
          risk_description:
            "Continuity of ignitable material provides fuel for propagating fires.\n\nLimiting the continuity of ignitable material through dedicated storage arrangements mitigates the risk of a fire spread.",
        },
        {
          code: "2022-7",
          title: "Improve electrical switch-room management practices.",
          priority: "Urgent",
          detail:
            "All electrical switch-rooms on site should:\n- Not be used for storage.\n- Be sealed off from the site and not contain any wall or ceiling penetrations.\n- Contain a smoke detector connected to a mimic panel or alarm in an audible location.",
          risk_description:
            "Electrical faults are a common cause of fires. \n\nDue to the initial, slow propagation of electrical fires, early detection could extinguish an electrical fire in its incipient stage, especially if the fire has not propagated to other switchboards or excess ignitable storage within the switch-room.\n\nEnsuring the switch-room is free of penetrations ensures that any smoke damage is confined the room itself, reducing the risk of smoke damage to other equipment and stock on site.",
        },
        {
          code: "2022-8",
          title: "Improve the preventative maintenance program.",
          priority: "Urgent",
          detail:
            "A preventative maintenance program should be instated for all major pieces of equipment used on site and should include:\n- Annual servicing\n- Routine maintenance as per the manufacturer’s specifications.\n- Adequate training of employees operating said equipment.\n\nSpares for all critical machinery should be kept on premises to ensure minimisation of unnecessary downtime.",
          risk_description:
            "Preventative maintenance is correlated to smooth functioning and longevity of equipment.\n\nMachinery breakdown with no critical spares on-hand would lead to business interruption and leave the site at the mercy of supply chains.",
        },
        {
          code: "2022-9",
          title: "Conduct Annual Thermographic Scanning.",
          priority: "Urgent",
          detail:
            "Thermographic scanning of the main electrical switchboards on site should be conducted annually.\n\nThermographic scanning is used to identify faulty electrical equipment and should be part of any preventative maintenance scheme. \n\nThermographic scans help identify incipient faults in electrical systems before they develop, thus enabling corrective action to be taken before a serious electrical failure occurs.",
          risk_description:
            "Electrical faults are one of the leading causes of fires. Major faults and arcing could lead to fire, rendering the site without power and even significantly damaging the site’s building and contents if the fire spreads to nearby ignitable material.",
        },
        {
          codeq: "2022-10",
          title: "Improve ignitable liquid operations.",
          priority: "Urgent",
          detail:
            "Containment, interlocks and automatic shutdowns should be provided for all ignitable liquid operations on site.\n\nIgnitable liquid operations could include:\n- Hydraulic oil units\n- Heat Transfer Fluid (HTF) units\n\nAutomatic shutdowns of these systems can be actuated through several mechanisms including loss of pressure of volume of the bulk liquid reservoirs.",
          risk_description:
            "Leakage of ignitable liquids under pressure could produce an atomised spray, making them easily ignitable. \n\nShould a spray fire occur, it will continue to burn until the fuel supply is stopped.",
        },
        {
          code: "2022-11",
          title: "Improve ignitable liquid storage.",
          priority: "Urgent",
          detail:
            "Ignitable liquid stores should contain the following risk mitigation measures:\n- Containment in the form of bunding.\n- Adequate drainage.\n- Adequate ventilation.",
          risk_description:
            "Ignitable liquid leakage could spread further than the confines of the storage area.\n\nLimiting fuel spread through bunding and drainage limits fire spread to other site areas in the event of ignition, whilst ventilation limits the build-up of ignitable vapours to prevent explosion.",
        },
        {
          code: "2022-12",
          title: "Separate ignitable material from ignition sources.",
          priority: "Urgent",
          detail:
            "Every effort should be made to relocate ignitable materials including plastics, wood, cardboard etc. at least 1.5m away from ignition sources. \n\nCommon ignition sources include:\n- electrical switchgear\n- forklift charging stations\n- heating equipment",
          risk_description:
            "Continuity of ignitable material provides fuel for propagating fires.\n\nLimiting the continuity of ignitable material through separation of the material itself and also with ignition sources mitigates the risk of a fire starting and subsequent fire spread.",
        },
        {
          code: "2022-13",
          title:
            "Conduct Dissolved Gas Analysis on all liquid insulated transformers.",
          priority: "Urgent",
          detail:
            "Annual dissolved gas analysis should be conducted on all liquid cooled transformers.",
          risk_description:
            "In most cases, large transformer losses involve fire, explosion, high-voltage electrical arcs, oil ignition and dispersion. \n\nA fire or explosion within a transformer could cause extensive property damage and downtime due to the loss of power to critical areas of the site.",
        },
        {
          code: "2022-14",
          title: "Relocate Idle Pallets Away from Buildings.",
          priority: "Urgent",
          detail:
            "Idle pallets should be stored at least 5m away from the face of buildings.",
          risk_description:
            "Due to a rich oxygen-to-fuel ratio, pallet fires propagate extremely quickly.\n\nThe intense release of heat has the potential to significantly damage the building’s façade, frame, stock and contents.\nStoring unused pallets away from the building’s wall reduces the risk of the a pallet fire being transferred to the rest of the building.",
        },
        {
          code: "2022-15",
          title: "Improve generator management practices.",
          priority: "Urgent",
          detail:
            "Annual generator services should be accompanied with monthly testing.\n\nAll generators on site should contain the following fire mitigation measures:\n\n- Adequate bunding to contain any fuel leakage from generator fuel tanks.\n- Interlocks on the fuel supply to shut-off pumps supplying fuel to the generator in the event of a fuel leak.",
          risk_description:
            "Fuel leakage from the generator’s fuel tank could spread further than the confines of its vicinity.\n\nLimiting fuel spread through bunding and interlocks limits fire spread to other site areas in the event of ignition.",
        },
        {
          code: "2022-16",
          title: "Install/ Extend a Fire Detection System",
          priority: "Urgent",
          detail:
            "Fire detection connected to a monitoring system and alarm should be installed on site.\n\nThe monitoring system should notify the fire brigade if smoke, heat or a fire is detected.",
          risk_description:
            "Detecting a fire promptly allows first responders to tend to the emergency.\n\nThe speed of response could prevent further fire spread throughout the site, preventing unnecessary property damage and business interruption.",
        },
        {
          code: "2022-17",
          title: "Lock all sprinkler control values in the open position.",
          priority: "Urgent",
          detail:
            "All sprinkler control valves should be locked with padlock and chain in the open position.",
          risk_description:
            "As the site’s first line of defence against fire, all water supplies to the automatic sprinkler system should be unobstructed for it to function appropriately.\n\nEnsuring all valves are locked in the open position ensures the sprinkler system is ready to respond in the event of a fire.",
        },
        {
          code: "2022-18",
          title: "Extend hydrant protection to protect all building on site.",
          priority: "Urgent",
          detail:
            "The hydrant system should be extended to protect all buildings on site.",
          risk_description:
            "The hydrant system is a critical component of the site’s protection against fire.\n\nInadequate hydrant protection would undermine firefighting efforts by the brigade in an emergency.",
        },
        {
          code: "2022-19",
          title: "Improve the hydrant water supply to site.",
          priority: "Urgent",
          detail:
            "The current hydrant water supply should be boosted, as it currently does not meet the demands of the hydrant system installed on site.\n\nFire pumps and tanks may need to be installed to boost the current water supply.",
          risk_description:
            "An adequate and reliable water supply is required for an effective hydrant system.\n\nAn inadequate and unreliable water supply would undermine firefighting efforts by the brigade in an emergency.",
        },
        {
          code: "2022-20",
          title: "Provide Sprinkler Flow Tests For Review.",
          priority: "Urgent",
          detail:
            "The annual sprinkler and hydrant flow test data should be provided to PlacedRisk Solutions for engineering review.",
          risk_description:
            "Flow testing the automatic sprinkler system’s water supply is critical in identifying any faults and ensure adequate water is available for the system to operate as designed.\n\nInadequate and unreliable water supplies could render the automatic sprinkler system ineffective in fighting fires.",
        },
        {
          code: "2022-21",
          title: "Improve fire detection and protection maintenance practices.",
          priority: "Urgent",
          detail:
            "All fire detection and protection systems including fire detectors, automatic sprinklers and hydrants should be maintained to Australian Standard AS 1851.\n\nMaintenance schedules include weekly checks and annual maintenance.",
          risk_description:
            "The fire detection and protection systems on site are its first automatic notification and defence against fires.\n\nInoperable systems could render fires unseen and lead to large blazes causing excessive property damage and business interruption.",
        },
        {
          code: "2022-22",
          title: "Instate an Emergency Response Plan (ERP).",
          priority: "Urgent",
          detail:
            "A formalised ERP should be instated on site containing the following components:\n- A fire brigade notifier to notify the fire brigade of the emergency.\n- Regular evacuation drills to ensure site personnel are familiar with evacuation procedures and muster points. This enables the fire brigade to promptly tend to the fire.\n- Familiarise the brigade with the site, including the location of fire-fighting equipment, water tanks, fire hydrants, fire pumps etc.\n- An updated emergency contact register.",
          risk_description:
            "Up-to-date and well rehearsed ERP’s enable first responders to tackle potential fires efficiently, limiting property damage and subsequent downtime.",
        },
        {
          code: "2022-23",
          title:
            "Provide containment and fire barriers between individual transformers.",
          priority: "Advisory",
          detail:
            "Multiple transformers on site have been located side-by-side and near buildings.\n\nA two-hour fire barrier should be installed between adjacent transformers.\n\nBarriers should also be installed between all transformers and buildings if the transformer is within 1.5m away from the building’s face.\n\nSpill containment bunds should be installed around all liquid insulated transformers.",
          risk_description:
            "Transformers present an unnecessary fire exposure to each other and adjacent buildings. \n\nIn most cases, large transformer losses involve fire, explosion, high-voltage electrical arcs, oil ignition and dispersion. \n\nA fire or explosion within one transformer could render the loss of adjacent transformers and cause significant damage to adjacent buildings. This would not only result in extensive property damage, but also significant downtime due to the loss of power to critical areas of the site.",
        },
        {
          code: "2022-24",
          title: "Instate Gas Fuel Train Checklists.",
          priority: "Advisory",
          detail:
            "All components of gas fuel trains on site should be individually checked and tested annually as part of routine maintenance.",
          risk_description:
            "A gas fuel train is required to achieve the optimal and safe operation of gas-fired industrial heating equipment.\n\nThis combustion safeguard prevents adverse conditions that lead to explosions and over-pressure events in equipment such as boilers and kilns.",
        },
        {
          code: "2022-25",
          title:
            "Install an interlocking system to shut down ammonia refrigeration",
          priority: "Advisory",
          detail:
            "Ammonia leak detection should be installed in the plant room and all refrigerated areas of the site.\n\nAn alarm triggering an automatic shut-down of the ammonia refrigeration system should a leak be detected.",
          risk_description:
            "Ammonia is an ignitable and toxic gas used for refrigeration.\n\nLack of ventilation could result in a concentrated ammonia build up from a potential leak.\n\nIgnition of concentrated ammonia could result in an explosion and subsequent fire, resulting in property damage to the immediate and surrounding areas.",
        },
        {
          code: "2022-26",
          title: "Instate a Flood Emergency Response Plan (FERP).",
          priority: "Advisory",
          detail:
            "As this site is exposed to a 1 in 100 year flooding event, a FERP should be instated on location.\n\nThis document should include:\n- A plausible flood scenario, which includes the expected water inundation on site.\n- Warnings of potential a flood (television, radio, local government, other reporting).\n- Persons in charge of activating the plan, including personnel responsible for the shutdown of equipment and essential services.\n- Clean-up and start-up duties to minimise business interruption post event.",
          risk_description:
            "Flooding on site could not only cause potential property damage, but also significant downtime if essential services are adversely affected.\n\nUnprepared facilities historically suffer unnecessary losses due to rapid rises in water levels.\n\nInstating a FERP allows management to identify potential areas at risk of flood and mitigate the impact of flooding in practical and responsive ways, long before flood waters inundate the site.",
        },
        {
          code: "2022-27",
          title: "Instate a Windstorm Emergency Response Plan (WERP)",
          priority: "Advisory",
          detail:
            "According to Munich RE’s Natural Hazards Assessment Network, this site is exposed to a cyclonic event.\n\nThis could mean the loss of parts of the site’s roof, along with windows and dock-doors.\n\nAs such, a WERP should be instated on location and should include:\n\n- A plausible windstorm scenario, which includes the expected water inundation on site.\n- Warnings of potential a windstorm (television, radio, local government, other reporting).\n- Persons in charge of activating the plan, including personnel responsible for the shutdown of equipment and essential services. \n- Clean-up and start-up duties to minimise business interruption post event.",
          risk_description:
            "If any part of this site’s building envelope is breached, stormwater could enter the building, damaging contents and stock, causing property damage.\n\nBusiness interruption to the site could be amplified if essential services are adversely affected.\n\nInstating a WERP allows management to identify potential areas of the site at risk of building breach and mitigate these in practicable ways.",
        },
        {
          code: "2022-28",
          title: "Replace all asbestos roofing.",
          priority: "Advisory",
          detail:
            "As this site is located in a hailstorm exposed area, all remaining asbestos roofing should be replaced.",
          risk_description:
            "A hailstorm could severely damage, puncture and disintegrate asbestos roofing on site.\n\nThis would not only result in the roof being replaced, but also the contamination of products and stock below.",
        },
        {
          code: "2022-29",
          title: "Instate a Bushfire Emergency Response Plan (BERP)",
          priority: "Advisory",
          detail:
            "As this site is surrounded by dense bushland, a BERP should be instated on location.\n\nThis document should include:\n\n- Precautions to prepare the site for bushfire season, including at least 15m clearance of vegetation from buildings.\n- A plausible bushfire scenario, which includes the expected affected buildings.\n- Warnings of potential a bushfire (television, radio, local government, other reporting).\n- Persons in charge of activating the plan, including personnel responsible for the shutdown of equipment and essential services. \n- Clean-up and start-up duties to minimise business interruption post event.",
          risk_description:
            "A bushfire event could lead to considerable loss of life and property damage onsite if the appropriate preparatory measures are not taken.\n\nInstating a BERP would allow site personnel to efficiently mitigate risks under their control before, during and after a bushfire.",
        },
        {
          code: "2022-30",
          title: "Instate an Earthquake Emergency Response Plan (EERP)",
          priority: "Advisory",
          detail:
            "As this site is exposed to earthquake, a EERP should be instated on location.\n\nAs earthquakes can provide little or no warning, preparation for a potential earthquake event remains the key to limit property damage and subsequent business interruption.\n \nThe EERP should include:\n\n- An assessment of a potential earthquake impact and intensity.\n- Determination of the structural integrity of the buildings on site.\n- A review of contents and stock that is prone to toppling and installing the appropriate seismic restraints.\n- Implementation of bracing and restraints where required.\n- Warnings of potential earthquake (television, radio, local government, other reporting).\n- Clean-up and start-up duties to minimise business interruption post event.",
          risk_description:
            "An earthquake could lead to considerable loss of life and property damage onsite if the appropriate preparatory measures are not taken.\n\nInstating an EERP would allow site personnel to efficiently mitigate risks under their control before, during and after an earthquake.",
        },
        {
          code: "2022-31",
          title: "Install/ Extend an Automatic Sprinkler System",
          priority: "Advisory",
          detail:
            "Automatic sprinklers are the primary active defence against a fire starting on site and the most reliable means of controlling fire.\nAn appropriately designed automatic sprinkler system should be installed on site, along with an adequate and reliable water supply.",
          risk_description:
            "Due to the continuity of ignitable contents on site, a small fire could quickly grow and engulf the facility, resulting in extensive damage to buildings, contents and stock.\nIt could also leave the affected sections inoperable until rebuilding is complete or a new site is found to operate from.\nA fully functioning automatic sprinkler system is constantly available (if properly maintained) and does not require human intervention to respond and contain a fire, ensuring that small fires are controlled or extinguished in their incipient phase.",
        },
        {
          code: "2022-32",
          title: "Update the Automatic Sprinkler System",
          priority: "Advisory",
          detail:
            "The current automatic sprinkler system was designed to protect a different occupancy and production process.\n\nAs the current occupancy is of a higher hazard than that of the designed system, the sprinkler system should be upgraded to protect the current hazard.",
          risk_description:
            "Sprinkler systems are designed to deliver enough water to a potential fire to either extinguish or control it to prevent spread.\n\nThe amount of water required for this is dependant on the intensity of the fire, which is in-turn dependant on the commodity stored or the production process.\n\nAn intense fire could overpower the current system and spread throughout the facility, leading to considerable property damage and subsequent business interruption.",
        },
        {
          code: "2022-33",
          title: "Improve the water supply to site.",
          priority: "Advisory",
          detail:
            "The current water supply should be boosted, as it currently does not meet the demands of the automatic sprinkler system installed on site.\n\nFire pumps and tanks may need to be installed to boost the current water supply.",
          risk_description:
            "An adequate and reliable water supply is required for effective sprinkler operation.\n\nA fully functioning automatic sprinkler system with an adequate and reliable water supply ensures that small fires are controlled or extinguished in their incipient phase, preventing further property loss and business interruption.",
        },
        {
          code: "2022-34",
          title: "Instate a formal Business Continuity Plan (BCP)",
          priority: "Advisory",
          detail:
            "Business Continuity Plans should be documented, rehearsed and kept up-to-date.\n\nThis document should include:\n- Predictable events that could render any downtime for the business, including fire, flood or loss of essential services.\n- Impacts of loss of suppliers and customers.\n- Communication to impacted customers.\n- Methods to supply critical customers during downtime, including appropriate logistics.\n- Contingency plans, including alternative sites to operate from until original facilities are operational.",
          risk_description:
            "Pre-prepared business continuity procedures could limit business interruption, loss of valued customers, and needless reputational damage during and after an adverse event.\n\nFormalising these plans give personnel on-deck a mechanism to save and resume business operations.",
        },
        {
          code: "N/A",
          title: "N/A",
          priority: "N/A",
          detail: "N/A",
          risk_description: "N/A",
        },
      ];

      let ri = [];
      if (q1 === "Not in Place") ri.push(riData[1]);
      if (q2 === "Not in Place") ri.push(riData[2]);
      if (q3 === "Not in Place") ri.push(riData[4]);
      if (q4 === "Not in Place") ri.push(riData[5]);
      if (q5 === "Not in Place") ri.push(riData[6]);
      if (q6 === "Not in Place") ri.push(riData[7]);
      if (q7 === "Not in Place") ri.push(riData[8]);
      if (q9 === "Not in Place") ri.push(riData[21]);
      if (q10 === "Not in Place") ri.push(riData[33]);
      ri.push(riData[34]);

      return ri;
    }

    const ri = returnRI(q1, q2, q3, q4, q5, q6, q7, q9, q10);

    const date = formatOrdinalDate(new Date());
    try {
      const json = {
        ...data,
        date,
        ri,
      };

      console.log("hazardarr", json);

      const response = await axiosSecond.post(
        `/pdfdownload/self-assessment/`,
        {
          data: json,
        }
      );
      if (response?.data?.message === "success") {
        setTimeout(async () => {
          const { data } = await axiosSecond.get(
            `/pdfdownload/self-assessment/upload`
          );
          setAssessmentPdf(data?.viewable_url);
          setAssessmentDownloadPdf(data?.downloadable_url);
          setLoading(false);
        }, 1000);
      } else {
        setLoading(false);
        setErrormg(true);
      }
    } catch (error) {
      console.error("Error occurred while downloading PDF:", error);
      setErrormg(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    get();
    // eslint-disable-next-line
  }, []);

  const sendEmail = async (event) => {
    event.preventDefault();
    if (!data?.email || !assessmentPdf || emailSending) {
      return;
    }

    setEmailSending(true);
    setEmailStatus(null);

    try {
      await axiosSecond.post("https://5ct754zdgi.execute-api.us-east-1.amazonaws.com/prsprod/user/reportemail", {
        email: data.email,
        assessmentPdf,
      });
      setEmailStatus({
        type: "success",
        text: `Your report has been sent to ${data.email}.`,
      });
    } catch (error) {
      console.error("Error sending email:", error);
      setEmailStatus({
        type: "error",
        text: "Unable to send your report by email. Please try again.",
      });
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="content-wrapper">
      <section
        className="banner-header valign bg-img"
        data-overlay-dark="2"
        style={{
          backgroundImage: "url('/img/banner.jpg')",
        }}
      >
        <div className="left-panel"></div>
      </section>
      <section className="bauen-project section-padding">
        <div className="container">
          <div className="row">
            <div className="col-md-12 mb-30 text-center animate-box" data-animate-effect="fadeInUp">
              <h2 className="section-title">
                Client <span>Self-Assessment</span>
              </h2>
            </div>
          </div>
          {loading ? (
            <Grid
              container
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Grid
                container
                item
                xs={10}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignContent: "center",
                }}
              >
                <Typography
                  style={{
                    textAlign: "center",
                    color: "#777",
                    fontFamily: '"Oswald", sans-serif',
                    fontSize: "18px",
                    fontWeight: 400,
                    letterSpacing: "2px",
                    padding: "0.7rem 1.2rem",
                  }}
                >
                  Your report is being generated by PRS AREA, our Analytical Risk Engineering Algorithm. You will be
                  auto-directed to the download page once your report is ready
                </Typography>
              </Grid>
              <Grid
                container
                item
                xs={12}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignContent: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "20rem",
                  }}
                >
                  <motion.div
                    animate={{
                      scale: [1, 1, 1, 1, 1],
                      rotate: [0, -360, -360, 0, 0],
                      borderRadius: ["20%", "20%", "50%", "50%", "50%", "50%", "20%", "20%"],
                    }}
                    transition={{
                      duration: 4,
                      ease: "easeInOut",
                      times: [0, 0.5, 0.5, 0.5, 0.5, 0.8, 1],
                      repeat: Infinity,
                      repeatDelay: 1,
                    }}
                    style={{
                      width: 120,
                      height: 120,
                      border: "3px solid rgb(34, 52, 91, 1)",
                      borderRadius: 15,
                      backgroundColor: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <motion.div
                      animate={{
                        scale: [1, 1.5, 1, 1, 1],
                        rotate: [0, 360, 360, 0, 0],
                        borderRadius: ["20%", "20%", "50%", "50%", "50%", "50%", "20%", "20%"],
                      }}
                      transition={{
                        duration: 4,
                        ease: "easeInOut",
                        times: [0, 0.5, 0.5, 0.5, 0.5, 0.8, 1],
                        repeat: Infinity,
                        repeatDelay: 1,
                      }}
                      style={{
                        width: 100,
                        height: 100,
                        border: "7px solid rgb(34, 52, 91, 0.25)",
                        borderRadius: 15,
                        backgroundColor: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        padding: "5px",
                        margin: "5px",
                      }}
                    >
                      <img
                        src="/img/logo.png"
                        alt="PRS Logo"
                        style={{
                          maxWidth: 80,
                          height: "auto",
                        }}
                      />
                    </motion.div>
                  </motion.div>
                  <Typography
                    sx={{
                      textAlign: "center",
                      marginTop: 4,
                      fontSize: "1rem",
                      color: "#666",
                    }}
                  >
                    Please wait while PRS AREA generates your report…
                  </Typography>
                </div>
              </Grid>
            </Grid>
          ) : errormg ? (
            <Grid
              container
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Grid
                container
                item
                xs={10}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignContent: "center",
                }}
              >
                <Typography
                  style={{
                    textAlign: "center",
                    color: "#d32f2f",
                    fontFamily: '"Oswald", sans-serif',
                    fontSize: "18px",
                    fontWeight: 400,
                    letterSpacing: "2px",
                    padding: "0.7rem 1.2rem",
                  }}
                >
                  Error: Unable to load assessment data. Please ensure you have valid data in the URL parameters.
                </Typography>
              </Grid>
            </Grid>
          ) : (
            assessmentPdf && (
              <div className="row">
                <div className="col-md-12">
                  <div className="projects4 animate-box" data-animate-effect="fadeInUp">
                    <figure>
                      <img src="/img/slider1.jpg" alt="" className="img-fluid" />
                    </figure>
                    <div className="caption" style={{ textAlign: "right" }}>
                      <form
                        style={{ paddingBottom: "20px" }}
                        onSubmit={(event) => {
                          event.preventDefault();
                          window.open(assessmentPdf);
                        }}
                      >
                        <div className="col-md-12">
                          <input type="submit" value="View your report" />
                        </div>
                      </form>
                      <form
                        style={{ paddingBottom: "20px" }}
                        onSubmit={(event) => {
                          event.preventDefault();
                          window.open(assessmentDownloadPdf);
                        }}
                      >
                        <div className="col-md-12">
                          <input type="submit" value="Download your report" />
                        </div>
                      </form>
                      {data?.email && (
                        <form onSubmit={sendEmail}>
                          <div className="col-md-12">
                            <button
                              type="submit"
                              disabled={emailSending}
                              style={{
                                color: "#fff",
                                fontFamily: '"Oswald", sans-serif',
                                fontSize: "16px",
                                fontWeight: 400,
                                letterSpacing: "5px",
                                textTransform: "uppercase",
                                padding: "0.7rem 1.2rem",
                                opacity: emailSending ? 0.7 : 1,
                                cursor: emailSending ? "not-allowed" : "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.75rem",
                              }}
                            >
                              {emailSending && (
                                <CircularProgress size={18} sx={{ color: "#fff" }} />
                              )}
                              {emailSending ? "Sending email..." : "Email your report"}
                            </button>
                          </div>
                          {emailStatus && (
                            <Typography
                              sx={{
                                marginTop: 1.5,
                                marginRight: "18px",
                                textAlign: "right",
                                fontSize: "14px",
                                color: emailStatus.type === "success" ? "#2e7d32" : "#d32f2f",
                              }}
                            >
                              {emailStatus.text}
                            </Typography>
                          )}
                        </form>
                      )}
                      <p style={{ marginRight: "18px", fontSize: "10px" }}>Powered by PRS AREA</p>
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </section>
    </div>
  );
};

export default PDFViewer;
